import React, { useState, useEffect, useMemo } from "react";
import { Button, SearchSelectOptionProps } from "@alm/flow-ds/components";
import { useDispatch, useSelector } from "react-redux";
import {
	createGetElementData,
	createGetElementListData,
	ElementComponentData,
	ListElementComponentData,
	prepSearchConfig,
} from "data/form/elementSelectors";
import { State } from "data";
import { ElementComponentProps } from "elements/types";
import { LookupElement } from "records/Element";
import { useStandardElement } from "components/Forms/Element";
import DisplayOnlyValue from "elements/DisplayOnlyValue";
import { useTranslation } from "react-i18next";
import useSearchSelect from "../../hooks/useSearchSelect";
import useOpenLinkedForm from "hooks/useOpenLinkedForm";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import api from "lib/api";
import _ from "lodash";
import { QueryClause } from "lib/api/types";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";
import SearchSelectAsyncPaginate from "./SearchSelectAsyncPaginate";

type LookupData =
	| (ElementComponentData<string> & { value: string })
	| ListElementComponentData<string>;

function LookupReactive({
	fid,
	vid,
	id,
	index,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	displayOnly,
	limit,
	search,
	searchOperator,
	isArray = false,
	removeable,
	createLink,
	name,
}: ElementComponentProps<LookupElement>) {
	const { t } = useTranslation();
	const [init, setInit] = useState(false);
	const dispatch = useDispatch();

	const getData = useMemo(createGetElementData, []);
	const getListData = useMemo(createGetElementListData, []);

	const selectorProps = useMemo(
		() => ({ fid, vid, id, index, rowIndex, uuid }),
		[fid, vid, id, index, rowIndex, uuid]
	);

	const data = useSelector((state: State) =>
		isArray
			? getListData(state, selectorProps)
			: getData(state, selectorProps)
	) as LookupData;

	const selectData = useMemo(() => {
		if (!data) {
			return isArray ? [] : undefined;
		}
		if ("items" in data) return data.items;

		if (!data.value) return undefined;

		return {
			value: data.value,
			label: data.label || data.value,
		};
	}, [data, isArray]);

	const searchConfig = useSelector((state: State) =>
		prepSearchConfig(state, selectorProps, search)
	);

	const selectProps = useMemo(
		() => ({ ...searchConfig, limit, searchOperator }),
		[limit, searchConfig, searchOperator]
	);

	const { eid, setDataAndQueue, remove } = useStandardElement(selectorProps);

	const { loadPageOptions, onChange, value, handleClearCache, cacheId } =
		useSearchSelect(selectProps, selectData);

	// HACK: This code fixes bls issue where it can't query to cdp. Remove this if bls gets updated
	// Run to query the cdp and get value for field if needed
	const loadInitialValue = async () => {
		// Create our query payload
		const payload = _.cloneDeep(searchConfig.payload);
		const pointerToSearch = jsonpointer.pointerToId(searchConfig.label);
		const filterQuery: QueryClause = [
			{ AND: [{ [pointerToSearch]: { exact: { value: data.value } } }] },
		];
		payload.filter.AND = [...filterQuery];
		try {
			const resp = await api.cursors.create(payload);
			if (resp.results.length > 0) {
				const newValue = {
					value: resp.results[0].id,
					label: _.isArray(data.value) ? data.value[0] : data.value,
				};
				onChange(isArray ? [newValue] : newValue);
			} else {
				onChange(null);
			}
		} catch (err) {
			console.error(err);
			dispatch(
				createAlertAction(
					createAlert({
						title: t("alerts.blsCursorError.title"),
						description: t("alerts.blsCursorError.description", {
							element: label,
						}),
						status: "error",
					})
				)
			);
		}
	};

	const { openLinkedForm } = useOpenLinkedForm(rowIndex, name);

	useEffect(() => {
		if (init) {
			if (isArray) {
				setDataAndQueue(data, {
					id,
					value: (value as SearchSelectOptionProps[])?.map(
						v => v.value
					),
				});
			} else {
				// Null = the value has been cleared
				if (value === null) {
					if (removeable) {
						remove();
					} else {
						// If removeable is false we set the value to null - this is legacyArrayView magic
						setDataAndQueue(data, { id, value: null });
					}
				} else {
					setDataAndQueue(data, {
						id,
						value: (value as SearchSelectOptionProps)?.value,
						label: undefined,
					});
				}
			}
		} else if (
			// TODO: Check this in tests when new testing branch is put through
			process.env.NODE_ENV !== "test" &&
			// If overwriteDataWith is set then we aren't using refernce values so don't make the call
			!(
				selectProps.overwriteDataWith !== undefined &&
				selectProps.overwriteDataWith !== null &&
				selectProps.overwriteDataWith.trim() !== ""
			) &&
			data !== undefined &&
			data !== null &&
			data.value !== undefined &&
			data.value !== null &&
			!_.isArray(data.value) &&
			!(data as SearchSelectOptionProps).value.includes("cdp:/")
		) {
			setInit(true);
			loadInitialValue();
		} else {
			setInit(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	// Display only
	if (displayOnly) {
		return (
			<DisplayOnlyValue
				label={label}
				value={data?.label || data?.value || ""}
				elem="lookup"
			/>
		);
	}

	// HACK: Change this back to SearchSelect from flow-ds when issue is fixed
	return (
		<>
			<SearchSelectAsyncPaginate
				hint={hint}
				required={required}
				disabled={disabled}
				onChange={onChange}
				label={label}
				limit={
					limit && isArray
						? t("main.text.limit", { limit })
						: undefined
				}
				multiselect={isArray}
				id={eid}
				status={data?.status}
				value={value}
				loadPageOptions={loadPageOptions}
				debounceTimeout={400}
				clearable={true}
				// feedback={}
				onMenuClose={handleClearCache}
				// When cacheId value has changed
				// then cache is cleared
				cacheUniqs={cacheId}
				footerButtons={
					createLink ? (
						<Button
							icon="plus"
							label={t("main.labels.create")}
							className={`btn-outline-primary ${
								disabled ? "disabled" : ""
							}`}
							onClick={() => openLinkedForm(index, true)}
							disabled={disabled}
						/>
					) : null
				}
			/>
		</>
	);
}

export default React.memo(LookupReactive);
